// ═══════════════════════════════════════════════════════════
// Debris Store — Zones, Tickets, Contractors, Trucks
// ═══════════════════════════════════════════════════════════

import { create } from 'zustand';
import { queryRecords, fetchRecords, createRecord, updateRecord, deleteRecord } from '../api/client';
import { TABLES } from '../api/tables';
import { useAuthStore } from '../auth/auth-store';
import { FIELDS } from '../api/fields';
import type { DebrisZone, DebrisLoadTicket, DebrisContractor, DebrisTruck, DebrisDisposalSite } from '../types/debris';
import type { QueryOptions } from '../api/types';

const FT = FIELDS.debrisLoadTickets;
const FZ = FIELDS.debrisZones;
const FC = FIELDS.debrisContractors;
const FTr = FIELDS.debrisTrucks;
const FS = FIELDS.debrisDisposalSites;

function mapTicket(r: Record<string, unknown>): DebrisLoadTicket {
  return {
    id: r.record_id as number, incidentId: (r[FT.incidentId] as number) || null,
    ticketNumber: (r[FT.ticketNumber] as string) || '', zoneId: (r[FT.zoneId] as number) || null,
    contractorId: (r[FT.contractorId] as number) || null, truckId: (r[FT.truckId] as number) || null,
    disposalSiteId: (r[FT.disposalSiteId] as number) || null,
    debrisType: (r[FT.debrisType] as string) || '', measurementMode: (r[FT.measurementMode] as string) || 'volumetric',
    calculatedCubicYards: Number(r[FT.calculatedCubicYards]) || 0, unitCount: Number(r[FT.unitCount]) || 0,
    weight: Number(r[FT.weight]) || 0, loadingLoadPct: Number(r[FT.loadingLoadPct]) || 0,
    towerLoadPct: Number(r[FT.towerLoadPct]) || 0, handLoaded: Boolean(r[FT.handLoaded]),
    pickupStopsJson: (r[FT.pickupStopsJson] as string) || '', status: (r[FT.status] as string) || 'draft',
    loadingMonitorName: (r[FT.loadingMonitorName] as string) || '',
    towerMonitorName: (r[FT.towerMonitorName] as string) || '',
    verifiedAt: (r[FT.verifiedAt] as string) || '', notes: (r[FT.notes] as string) || '',
  };
}

function mapZone(r: Record<string, unknown>): DebrisZone {
  return {
    id: r.record_id as number, incidentId: (r[FZ.incidentId] as number) || null,
    zoneName: (r[FZ.zoneName] as string) || '', zoneCode: (r[FZ.zoneCode] as string) || '',
    geometry: (r[FZ.geometry] as string) || '', centroidLat: (r[FZ.centroidLat] as number) || null,
    centroidLng: (r[FZ.centroidLng] as number) || null, estimatedCY: Number(r[FZ.estimatedCY]) || 0,
    actualCY: Number(r[FZ.actualCY]) || 0, currentPass: (r[FZ.currentPass] as string) || '',
    passHistory: (r[FZ.passHistory] as string) || '', status: (r[FZ.status] as string) || 'active',
    notes: (r[FZ.notes] as string) || '',
  };
}

function mapContractor(r: Record<string, unknown>): DebrisContractor {
  return {
    id: r.record_id as number, incidentId: (r[FC.incidentId] as number) || null,
    companyName: (r[FC.companyName] as string) || '', contractNumber: (r[FC.contractNumber] as string) || '',
    contactName: (r[FC.contactName] as string) || '', contactPhone: (r[FC.contactPhone] as string) || '',
    contactEmail: (r[FC.contactEmail] as string) || '', contractAmount: Number(r[FC.contractAmount]) || 0,
    status: (r[FC.status] as string) || 'active', notes: (r[FC.notes] as string) || '',
  };
}

function mapTruck(r: Record<string, unknown>): DebrisTruck {
  return {
    id: r.record_id as number, contractorId: (r[FTr.contractorId] as number) || null,
    truckNumber: (r[FTr.truckNumber] as string) || '', licensePlate: (r[FTr.licensePlate] as string) || '',
    truckType: (r[FTr.truckType] as string) || '', capacityCY: Number(r[FTr.capacityCY]) || 0,
    hasTailgate: Boolean(r[FTr.hasTailgate]), certStatus: (r[FTr.certStatus] as string) || 'pending',
    certifiedDate: (r[FTr.certifiedDate] as string) || '', certExpiresAt: (r[FTr.certExpiresAt] as string) || '',
    placardNumber: (r[FTr.placardNumber] as string) || '', driverName: (r[FTr.driverName] as string) || '',
    driverPhone: (r[FTr.driverPhone] as string) || '', notes: (r[FTr.notes] as string) || '',
  };
}

function mapSite(r: Record<string, unknown>): DebrisDisposalSite {
  return {
    id: r.record_id as number, incidentId: (r[FS.incidentId] as number) || null,
    siteName: (r[FS.siteName] as string) || '', siteType: (r[FS.siteType] as string) || '',
    address: (r[FS.address] as string) || '', latitude: (r[FS.latitude] as number) || null,
    longitude: (r[FS.longitude] as number) || null, totalCapacity: Number(r[FS.totalCapacity]) || 0,
    currentVolume: Number(r[FS.currentVolume]) || 0, acceptedTypes: (r[FS.acceptedTypes] as string) || '',
    operatorName: (r[FS.operatorName] as string) || '', operatorPhone: (r[FS.operatorPhone] as string) || '',
    status: (r[FS.status] as string) || 'active', notes: (r[FS.notes] as string) || '',
  };
}

interface DebrisState {
  // Load Tickets (paginated)
  tickets: DebrisLoadTicket[];
  ticketsTotal: number;
  ticketsLoading: boolean;
  ticketsPage: number;
  fetchTickets: (opts?: Partial<QueryOptions>) => Promise<void>;
  setTicketsPage: (page: number) => void;

  // Zones (small table)
  zones: DebrisZone[];
  zonesLoading: boolean;
  fetchZones: () => Promise<void>;

  // Contractors (small table)
  contractors: DebrisContractor[];
  fetchContractors: () => Promise<void>;

  // Trucks (medium table)
  trucks: DebrisTruck[];
  fetchTrucks: () => Promise<void>;

  // Disposal sites (small table)
  disposalSites: DebrisDisposalSite[];
  fetchDisposalSites: () => Promise<void>;

  // CRUD
  createTicket: (data: Record<string, unknown>) => Promise<number | null>;
  updateTicket: (id: number, data: Record<string, unknown>) => Promise<void>;
  deleteTicket: (id: number) => Promise<void>;
  createZone: (data: Record<string, unknown>) => Promise<number | null>;
  updateZone: (id: number, data: Record<string, unknown>) => Promise<void>;
  createContractor: (data: Record<string, unknown>) => Promise<number | null>;
  updateContractor: (id: number, data: Record<string, unknown>) => Promise<void>;
  createTruck: (data: Record<string, unknown>) => Promise<number | null>;
  updateTruck: (id: number, data: Record<string, unknown>) => Promise<void>;
  deleteTruck: (id: number) => Promise<void>;
  createDisposalSite: (data: Record<string, unknown>) => Promise<number | null>;
  updateDisposalSite: (id: number, data: Record<string, unknown>) => Promise<void>;
  deleteZone: (id: number) => Promise<void>;
}

function zoneToPayload(data: Record<string, unknown>): Record<string, unknown> {
  const p: Record<string, unknown> = {};
  if (data.zoneName !== undefined) p[FZ.zoneName] = data.zoneName;
  if (data.zoneCode !== undefined) p[FZ.zoneCode] = data.zoneCode;
  if (data.estimatedCY !== undefined) p[FZ.estimatedCY] = data.estimatedCY;
  if (data.currentPass !== undefined) p[FZ.currentPass] = data.currentPass;
  if (data.status !== undefined) p[FZ.status] = data.status;
  if (data.incidentId !== undefined) p[FZ.incidentId] = data.incidentId;
  if (data.geometry !== undefined) p[FZ.geometry] = data.geometry;
  if (data.notes !== undefined) p[FZ.notes] = data.notes;
  return p;
}

function contractorToPayload(data: Record<string, unknown>): Record<string, unknown> {
  const p: Record<string, unknown> = {};
  if (data.companyName !== undefined) p[FC.companyName] = data.companyName;
  if (data.contractNumber !== undefined) p[FC.contractNumber] = data.contractNumber;
  if (data.contactName !== undefined) p[FC.contactName] = data.contactName;
  if (data.contactPhone !== undefined) p[FC.contactPhone] = data.contactPhone;
  if (data.contactEmail !== undefined) p[FC.contactEmail] = data.contactEmail;
  if (data.contractAmount !== undefined) p[FC.contractAmount] = data.contractAmount;
  if (data.status !== undefined) p[FC.status] = data.status;
  if (data.incidentId !== undefined) p[FC.incidentId] = data.incidentId;
  if (data.notes !== undefined) p[FC.notes] = data.notes;
  return p;
}

export const useDebrisStore = create<DebrisState>((set, get) => ({
  tickets: [], ticketsTotal: 0, ticketsLoading: false, ticketsPage: 1,
  zones: [], zonesLoading: false,
  contractors: [],
  trucks: [],
  disposalSites: [],

  fetchTickets: async (opts) => {
    set({ ticketsLoading: true });
    try {
      const { ticketsPage } = get();
      const inc = useAuthStore.getState().activeIncident;
      const incFilter = inc ? [{ field: FT.incidentId, operator: 'eq' as const, value: inc.id }] : [];
      const existingFilters = opts?.filters || [];
      const res = await queryRecords<Record<string, unknown>>(TABLES.debrisLoadTickets, {
        limit: 50, offset: (ticketsPage - 1) * 50,
        sort: { field: 'created_at', direction: 'desc' }, ...opts,
        filters: [...incFilter, ...existingFilters],
      });
      set({ tickets: res.records.map(mapTicket), ticketsTotal: res.total });
    } catch (e) { console.error('[Debris] fetchTickets', e); }
    set({ ticketsLoading: false });
  },

  setTicketsPage: (page) => { set({ ticketsPage: page }); get().fetchTickets(); },

  fetchZones: async () => {
    set({ zonesLoading: true });
    try {
      const inc = useAuthStore.getState().activeIncident;
      const incFilter = inc ? [{ field: FZ.incidentId, operator: 'eq' as const, value: inc.id }] : [];
      const res = incFilter.length > 0
        ? await queryRecords<Record<string, unknown>>(TABLES.debrisZones, { limit: 200, filters: incFilter })
        : await fetchRecords<Record<string, unknown>>(TABLES.debrisZones, { limit: 200 });
      set({ zones: res.records.map(mapZone) });
    } catch (e) { console.error('[Debris] fetchZones', e); }
    set({ zonesLoading: false });
  },

  fetchContractors: async () => {
    try {
      const res = await fetchRecords<Record<string, unknown>>(TABLES.debrisContractors, { limit: 100 });
      set({ contractors: res.records.map(mapContractor) });
    } catch (e) { console.error('[Debris] fetchContractors', e); }
  },

  fetchTrucks: async () => {
    try {
      const res = await fetchRecords<Record<string, unknown>>(TABLES.debrisTrucks, { limit: 500 });
      set({ trucks: res.records.map(mapTruck) });
    } catch (e) { console.error('[Debris] fetchTrucks', e); }
  },

  fetchDisposalSites: async () => {
    try {
      const res = await fetchRecords<Record<string, unknown>>(TABLES.debrisDisposalSites, { limit: 100 });
      set({ disposalSites: res.records.map(mapSite) });
    } catch (e) { console.error('[Debris] fetchDisposalSites', e); }
  },

  createTicket: async (data) => {
    try {
      const res = await createRecord<Record<string, unknown>>(TABLES.debrisLoadTickets, data);
      await get().fetchTickets();
      return (res as any).record_id;
    } catch (e) { console.error('[Debris] createTicket', e); return null; }
  },
  updateTicket: async (id, data) => { try { await updateRecord(TABLES.debrisLoadTickets, id, data); await get().fetchTickets(); } catch (e) { console.error('[Debris] updateTicket', e); } },
  deleteTicket: async (id) => { try { await deleteRecord(TABLES.debrisLoadTickets, id); await get().fetchTickets(); } catch (e) { console.error('[Debris] deleteTicket', e); } },
  createZone: async (data) => { try { const r = await createRecord<Record<string, unknown>>(TABLES.debrisZones, zoneToPayload(data)); await get().fetchZones(); return (r as any).record_id; } catch (e) { console.error('[Debris] createZone', e); return null; } },
  updateZone: async (id, data) => { try { await updateRecord(TABLES.debrisZones, id, zoneToPayload(data)); await get().fetchZones(); } catch (e) { console.error('[Debris] updateZone', e); } },
  createContractor: async (data) => { try { const r = await createRecord<Record<string, unknown>>(TABLES.debrisContractors, contractorToPayload(data)); await get().fetchContractors(); return (r as any).record_id; } catch (e) { console.error('[Debris] createContractor', e); return null; } },
  updateContractor: async (id, data) => { try { await updateRecord(TABLES.debrisContractors, id, contractorToPayload(data)); await get().fetchContractors(); } catch (e) { console.error('[Debris] updateContractor', e); } },
  createTruck: async (data) => {
    const p: Record<string, unknown> = {};
    if (data.truckNumber !== undefined) p[FTr.truckNumber] = data.truckNumber;
    if (data.licensePlate !== undefined) p[FTr.licensePlate] = data.licensePlate;
    if (data.truckType !== undefined) p[FTr.truckType] = data.truckType;
    if (data.capacityCY !== undefined) p[FTr.capacityCY] = data.capacityCY;
    if (data.hasTailgate !== undefined) p[FTr.hasTailgate] = data.hasTailgate;
    if (data.certStatus !== undefined) p[FTr.certStatus] = data.certStatus;
    if (data.driverName !== undefined) p[FTr.driverName] = data.driverName;
    if (data.driverPhone !== undefined) p[FTr.driverPhone] = data.driverPhone;
    if (data.contractorId !== undefined) p[FTr.contractorId] = data.contractorId;
    if (data.placardNumber !== undefined) p[FTr.placardNumber] = data.placardNumber;
    if (data.notes !== undefined) p[FTr.notes] = data.notes;
    try { const r = await createRecord<Record<string, unknown>>(TABLES.debrisTrucks, p); await get().fetchTrucks(); return (r as any).record_id; } catch (e) { console.error('[Debris] createTruck', e); return null; }
  },
  updateTruck: async (id, data) => {
    const p: Record<string, unknown> = {};
    if (data.truckNumber !== undefined) p[FTr.truckNumber] = data.truckNumber;
    if (data.licensePlate !== undefined) p[FTr.licensePlate] = data.licensePlate;
    if (data.capacityCY !== undefined) p[FTr.capacityCY] = data.capacityCY;
    if (data.hasTailgate !== undefined) p[FTr.hasTailgate] = data.hasTailgate;
    if (data.certStatus !== undefined) p[FTr.certStatus] = data.certStatus;
    if (data.driverName !== undefined) p[FTr.driverName] = data.driverName;
    if (data.contractorId !== undefined) p[FTr.contractorId] = data.contractorId;
    if (data.placardNumber !== undefined) p[FTr.placardNumber] = data.placardNumber;
    if (data.notes !== undefined) p[FTr.notes] = data.notes;
    try { await updateRecord(TABLES.debrisTrucks, id, p); await get().fetchTrucks(); } catch (e) { console.error('[Debris] updateTruck', e); }
  },
  deleteTruck: async (id) => { try { await deleteRecord(TABLES.debrisTrucks, id); await get().fetchTrucks(); } catch (e) { console.error('[Debris] deleteTruck', e); } },
  createDisposalSite: async (data) => {
    const p: Record<string, unknown> = {};
    if (data.siteName !== undefined) p[FS.siteName] = data.siteName;
    if (data.siteType !== undefined) p[FS.siteType] = data.siteType;
    if (data.address !== undefined) p[FS.address] = data.address;
    if (data.latitude !== undefined) p[FS.latitude] = data.latitude;
    if (data.longitude !== undefined) p[FS.longitude] = data.longitude;
    if (data.totalCapacity !== undefined) p[FS.totalCapacity] = data.totalCapacity;
    if (data.acceptedTypes !== undefined) p[FS.acceptedTypes] = data.acceptedTypes;
    if (data.operatorName !== undefined) p[FS.operatorName] = data.operatorName;
    if (data.operatorPhone !== undefined) p[FS.operatorPhone] = data.operatorPhone;
    if (data.status !== undefined) p[FS.status] = data.status;
    if (data.incidentId !== undefined) p[FS.incidentId] = data.incidentId;
    if (data.notes !== undefined) p[FS.notes] = data.notes;
    try { const r = await createRecord<Record<string, unknown>>(TABLES.debrisDisposalSites, p); await get().fetchDisposalSites(); return (r as any).record_id; } catch (e) { console.error('[Debris] createSite', e); return null; }
  },
  updateDisposalSite: async (id, data) => {
    const p: Record<string, unknown> = {};
    if (data.siteName !== undefined) p[FS.siteName] = data.siteName;
    if (data.siteType !== undefined) p[FS.siteType] = data.siteType;
    if (data.address !== undefined) p[FS.address] = data.address;
    if (data.totalCapacity !== undefined) p[FS.totalCapacity] = data.totalCapacity;
    if (data.operatorName !== undefined) p[FS.operatorName] = data.operatorName;
    if (data.status !== undefined) p[FS.status] = data.status;
    if (data.notes !== undefined) p[FS.notes] = data.notes;
    try { await updateRecord(TABLES.debrisDisposalSites, id, p); await get().fetchDisposalSites(); } catch (e) { console.error('[Debris] updateSite', e); }
  },
  deleteZone: async (id) => { try { await deleteRecord(TABLES.debrisZones, id); await get().fetchZones(); } catch (e) { console.error('[Debris] deleteZone', e); } },

  // V1 compat aliases
  loadZones: async () => get().fetchZones(),
  loadContractors: async () => get().fetchContractors(),
  loadTrucks: async () => get().fetchTrucks(),
  loadDisposalSites: async () => get().fetchDisposalSites(),
  loadingZones: false,
  loadingSites: false,
  crews: [] as any[],
  loadCrews: async () => {},
  createContractor: async (_data: any) => 0,
  createTruck: async (_data: any) => 0,
  createCrew: async (_data: any) => 0,
  deleteContractor: async (_id: any) => {},
  loadingContractors: false,
  deleteCrew: async (_id: any) => {},
  loadingCrews: false,
  updateCrew: async (_id: any, _data: any) => {},
  deleteDisposalSite: async (_id: any) => {},
  loadingTrucks: false,
  documents: [] as any[],
  loadDocuments: async () => {},
  loadingDocs: false,
  createDocument: async (_data: any) => null,
  updateDocument: async (_id: any, _data: any) => {},
  deleteDocument: async (_id: any) => {},
}));
